import {
  Box,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ClearIcon from "@mui/icons-material/Clear";
import { PlanData } from "./PricingGrid";
import CoreCard from "../core/CoreCard";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "24px",
  borderRightWidth: 1,
  borderRightColor: theme.palette.grey[300],
  borderRightStyle: "solid",
  textAlign: "center",
  minWidth: "300px",
  "&:last-child": {
    borderRight: "none",
  },
  maxWidth: "400px",
}));

const StyledTableRow = styled(TableRow)({
  "&:last-child td": {
    borderBottom: "none",
  },
});

const HeaderCell: React.FC<{
  plan: PlanData;
}> = ({ plan }) => {
  return (
    <StyledTableCell>
      <Typography variant="h5" color={plan.color} sx={{ fontFamily: "Open Sans Bold" }}>
        {plan.title}
      </Typography>
    </StyledTableCell>
  );
};

const FeaturesCell: React.FC<{ label: string; desc: string }> = ({ label, desc }) => {
  return (
    <StyledTableCell>
      <Stack>
        <Typography align="left" fontFamily="Open Sans Semi Bold" color="primary">
          {label}
        </Typography>
        {desc && (
          <Typography align="left" color="#333">
            {desc}
          </Typography>
        )}
      </Stack>
    </StyledTableCell>
  );
};

const ItemCell: React.FC<{
  featureLabel: string;
  comparison: PlanData["featureComparisons"];
}> = ({ featureLabel, comparison }) => {
  const foundFeature = comparison?.find((item) => item.label === featureLabel);
  const getFeatureDisplay = () => {
    if (foundFeature) {
      if (foundFeature.value === true) {
        return <CheckCircleIcon color="secondary" fontSize="large" />;
      }
      if (foundFeature.value === false) {
        return <ClearIcon fontSize="large" />;
      }
      return <Typography fontFamily="Open Sans Bold">{foundFeature.value}</Typography>;
    }
    return <ClearIcon fontSize="large" />;
  };
  return <StyledTableCell>{getFeatureDisplay()}</StyledTableCell>;
};

interface Props {
  plans: PlanData[];
}

const PricingComparisonChart: React.FC<Props> = ({ plans }) => {
  const combinedFeatures = plans.flatMap((plan) => {
    return plan.featureComparisons;
  });
  const uniqueByLabel: { [key: string]: string } = {};
  plans.forEach((plan) => {
    plan.featureComparisons?.forEach((item) => {
      uniqueByLabel[item.label] = uniqueByLabel[item.label] || (item.desc as string);
    });
  });

  return (
    <CoreCard padding={0}>
      <Box
        sx={{
          overflow: "auto",
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell></StyledTableCell>
              {plans.map((plan) => (
                <HeaderCell key={plan.type} plan={plan} />
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {Object.entries(uniqueByLabel).map(([label, desc]) => (
              <StyledTableRow key={label}>
                <FeaturesCell label={label} desc={desc} />
                {plans.map((plan) => (
                  <ItemCell
                    key={plan.type}
                    featureLabel={label}
                    comparison={plan.featureComparisons}
                  />
                ))}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </CoreCard>
  );
};

export default PricingComparisonChart;
